import { useCreateConversation } from 'hooks/useCreateConversation';
import { useTranslation } from 'react-i18next';

import React, { UIEvent, useCallback, useEffect, useRef, useState } from 'react';

import { LoadingSpinner } from '@thinkific/chalk/LoadingSpinner';
import { Modal } from '@thinkific/chalk/Modal';
import { SearchField } from '@thinkific/chalk/SearchField';
import { Text } from '@thinkific/chalk/Text';
import { MenuItemWithSymbols } from '@thinkific/chalk/recipes/MenuItemWithSymbols';

import { useCommunityContext } from 'components/CommunityContext';
import UserAvatar from 'components/UserAvatar';

import { useChatContext } from '../chat/providers/ChatProvider/ChatProvider';
import * as css from './NewConversationModal.css';
import { UserResult, useDMUsersSearch } from './hooks/useDMUsersSearch';

const NewConversationModal = () => {
  const { t } = useTranslation();
  const { isNewChannelModalOpen, toggleNewConversationModal } = useChatContext();
  const { currentUser } = useCommunityContext();
  const { createConversationWithUser, navigateToConversation, loadingConversationUserId } =
    useCreateConversation();

  // Use the DM-specific users search hook
  const { handleSearch } = useDMUsersSearch();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchableUsers, setSearchableUsers] = useState<UserResult[]>([]);
  const searchFieldRef = useRef<HTMLInputElement>(null);

  const handleModalClose = () => {
    setSearchTerm('');
    toggleNewConversationModal();
  };

  useEffect(() => {
    if (searchTerm.trim()) {
      handleSearch(searchTerm).then((users) => {
        setSearchableUsers(users.filter((user) => user.gid !== currentUser.gid));
      });
    } else {
      setSearchableUsers([]);
    }
  }, [searchTerm, currentUser.gid]);

  // Focus the search field when the modal opens
  useEffect(() => {
    if (isNewChannelModalOpen && searchFieldRef.current) {
      // Add a small delay to ensure the modal is fully rendered
      const timeoutId = setTimeout(() => {
        searchFieldRef.current?.focus();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [isNewChannelModalOpen]);

  const handleAddMember = async (user) => {
    const conversationId = await createConversationWithUser(user.gid);
    if (conversationId) {
      navigateToConversation(conversationId);
      toggleNewConversationModal();
    }
  };

  const handleScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  return (
    <Modal
      open={isNewChannelModalOpen}
      size="md"
      title={t('components-NewConversationModal-start_a_conversation', 'Start a conversation')}
      onClose={handleModalClose}
    >
      <div className={css.container}>
        <SearchField
          className={css.searchField}
          dataQa="create-conversation-modal-search-field"
          placeholder={t(
            'add-members-to-space-search-by-name-input',
            'Search by first or last name'
          )}
          ref={searchFieldRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e)}
          onClear={() => setSearchTerm('')}
        />

        <div className={css.resultsContainer} onScroll={handleScroll}>
          {searchableUsers.map((user) => (
            <MenuItemWithSymbols
              endSymbol={loadingConversationUserId === user.gid && <LoadingSpinner />}
              key={user.gid}
              label={user.display}
              leadSymbol={
                <UserAvatar
                  avatarUrl={user.avatarUrl}
                  size="xs"
                  userId={user.gid}
                  username={user.display}
                />
              }
              onClick={() => handleAddMember(user)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddMember(user);
                }
              }}
            />
          ))}
          {searchableUsers.length === 0 && (
            <div className={css.emptyState}>
              <Text>
                {searchTerm.trim()
                  ? t(
                      'components-NewConversationModal-no_matching_users_found',
                      'No matching users found'
                    )
                  : t(
                      'components-NewConversationModal-start_typing_to_search',
                      'Start typing to search for users'
                    )}
              </Text>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default NewConversationModal;
